import { Avatar, Dropdown, Grid, MenuProps, Modal, Skeleton, Switch, Typography, message, type DropdownProps } from 'antd';
import {
  BgColorsOutlined,
  DesktopOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  GlobalOutlined,
  LogoutOutlined,
  MoonFilled,
  QuestionCircleOutlined,
  RollbackOutlined,
  ShoppingCartOutlined,
  SunFilled,
  SwapOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ButtonPanelConfigEstilo, type ButtonPanelConfigEstiloRef } from '@components/settings/RenderEstiloConfig.tsx';
import { useCmsAccess } from '@features/cms/hooks/useCmsAccess.ts';
import LoginButton from '@login/pages/LoginButton.tsx';
import { useCheckoutLayout } from '@eshop/hooks/useCheckoutLayout.tsx';
import { useErpAdvancedMode } from '@erp/overview/useErpAdvancedMode.ts';
import { selectErpEnabled, selectSiteApps } from '@store/slices/settingsSlice.tsx';
import {
  fetchMyRelationshipsAsync,
  logout,
  returnToOriginAsync,
  selectAuthRelationships,
  switchProfileAsync,
} from '@store/slices/authSlice.tsx';
import { fetchProfile, selectProfile, selectProfileStatus, selectUserPermissions } from '@store/slices/userSlice';
import { resetApp } from '@store/resetAction.ts';
import { persistor, useAppDispatch, useAppSelector } from '@store/store.tsx';
import type { ThemeMode } from '@store/slices/uiSlice.ts';
import { useThemeMode } from '@theme/hooks/useThemeMode.tsx';
import { logInfo } from '@tools/LogsHandler.tsx';
import { resetQueryClient } from '@tools/queryClient.ts';
import { usePublicSitePreview } from '../../../contexts/PublicSitePreviewContext.tsx';
import { useDraftOverrides } from '@eshop/templates/context/DraftOverridesContext';
import { startTour } from '@store/slices/tourSlice.tsx';
import { getValidTourSteps, scrollToFirstElement } from '@features/eshop/components/tours/Utils.tsx';
import type { TourStep } from '@app-types/tour.types.ts';

import './UserMenu.css';

const MENU_REOPEN_GUARD_MS = 350;

interface UserMenuProps {
  children?: React.ReactNode;
  placement?: DropdownProps['placement'];
}

export const UserMenu: React.FC<UserMenuProps> = ({ children, placement }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const profileStatus = useAppSelector(selectProfileStatus);
  const profile = useAppSelector(selectProfile);
  const permissions = useAppSelector(selectUserPermissions);
  const relationships = useAppSelector(selectAuthRelationships);
  const { checkoutMode, hideNavigation, staffBypassEnabled, setStaffBypassEnabled } = useCheckoutLayout();
  const cmsAccess = useCmsAccess();
  const siteApps = useAppSelector((state) => selectSiteApps({ settings: state.settings })) ?? [];
  const erpEnabled = useAppSelector(selectErpEnabled);
  const { advancedMode, setAdvancedMode } = useErpAdvancedMode();
  const { themeMode, setThemeMode } = useThemeMode();
  const { previewActive, exitPreview } = usePublicSitePreview();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [openMenuKeys, setOpenMenuKeys] = useState<string[]>([]);
  const [isColorModalOpen, setIsColorModalOpen] = useState(false);
  const [isSavingColors, setIsSavingColors] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const colorPanelRef = useRef<ButtonPanelConfigEstiloRef>(null);
  const suppressNextOpenRef = useRef(false);
  const suppressNextOpenTimerRef = useRef<number | null>(null);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  // Get draft state to show editor option status
  const draftOverrides = useDraftOverrides();
  const isEditorMinimized = draftOverrides?.isMinimized ?? false;
  const hasUnsavedChanges = draftOverrides?.hasUnsavedChanges ?? false;

  // Guided tours logic
  const stepsRawList = useAppSelector((state) => state.tour?.stepsRawList ?? {});
  const [validTours, setValidTours] = useState<Array<{ tourName: string; stepsRaw: TourStep[] }>>([]);

  useEffect(() => {
    const toursWithValidSteps = Object.entries(stepsRawList)
      .map(([tourName, stepsRaw]) => {
        const validSteps = getValidTourSteps(stepsRaw ?? []) ?? [];
        return { tourName, stepsRaw: validSteps };
      })
      .filter((tour) => tour.stepsRaw.length > 0);

    setValidTours(toursWithValidSteps);
  }, [stepsRawList]);

  const handleTourSelection = useCallback(
    async (stepsRaw: TourStep[]) => {
      const validSteps = getValidTourSteps(stepsRaw) ?? stepsRaw;
      await scrollToFirstElement(validSteps);
      dispatch(startTour({ steps: validSteps }));
    },
    [dispatch],
  );

  const handleEditTiendaClick = useCallback(() => {
    const timestamp = Date.now();
    const basePath = '/eshop/catalogue';
    const currentPath = location.pathname;

    if (currentPath.startsWith(basePath)) {
      navigate(`${currentPath}?editTemplate=1&_t=${timestamp}`, { replace: true });
    } else {
      navigate(`${basePath}?editTemplate=1&_t=${timestamp}`);
    }
  }, [location.pathname, navigate]);

  const handleSaveColors = async () => {
    if (!colorPanelRef.current) return;
    setIsSavingColors(true);
    try {
      await colorPanelRef.current.saveColors();
    } finally {
      setIsSavingColors(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && !profile) {
      dispatch(fetchProfile());
      logInfo('selectProfile: ', selectProfile, 's.user.profile', profile);
    }
  }, [isAuthenticated, profile, dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      void dispatch(fetchMyRelationshipsAsync());
    }
  }, [isAuthenticated, dispatch]);

  const isStaff = profile?.role?.includes('staff') || profile?.role?.includes('superuser');
  const canManageRoles = permissions.includes('roles:manage');

  const isErpContext = location.pathname.startsWith('/erp');
  const isDashboardContext = location.pathname.startsWith('/dashboard');
  const isCmsContext = location.pathname.startsWith('/cms')
    || location.pathname.startsWith('/web')
    || location.pathname.startsWith('/site-builder');
  const showAdvancedToggle = (isErpContext || isDashboardContext) && isStaff;
  const themeModeLabel = themeMode === 'dark' ? 'Oscuro' : themeMode === 'system' ? 'Sistema' : 'Claro';
  const themeModeIcon = themeMode === 'dark'
    ? <MoonFilled />
    : themeMode === 'system'
      ? <DesktopOutlined />
      : <SunFilled />;

  const allowStaffOverride = checkoutMode && isStaff;

  const closeUserMenu = useCallback((options?: { suppressImmediateReopen?: boolean }) => {
    if (options?.suppressImmediateReopen) {
      suppressNextOpenRef.current = true;
      if (suppressNextOpenTimerRef.current !== null) {
        window.clearTimeout(suppressNextOpenTimerRef.current);
      }
      suppressNextOpenTimerRef.current = window.setTimeout(() => {
        suppressNextOpenRef.current = false;
        suppressNextOpenTimerRef.current = null;
      }, MENU_REOPEN_GUARD_MS);
    }
    setOpenMenuKeys([]);
    setIsUserMenuOpen(false);
  }, []);

  const handleUserMenuOpenChange: DropdownProps['onOpenChange'] = useCallback((open: boolean) => {
    if (open && suppressNextOpenRef.current) {
      suppressNextOpenRef.current = false;
      if (suppressNextOpenTimerRef.current !== null) {
        window.clearTimeout(suppressNextOpenTimerRef.current);
        suppressNextOpenTimerRef.current = null;
      }
      return;
    }
    setIsUserMenuOpen(open);
    if (!open) {
      setOpenMenuKeys([]);
    }
  }, []);

  useEffect(() => () => {
    if (suppressNextOpenTimerRef.current !== null) {
      window.clearTimeout(suppressNextOpenTimerRef.current);
    }
  }, []);

  const handleMenuOpenChange: MenuProps['onOpenChange'] = useCallback((keys: string[]) => {
    setOpenMenuKeys(keys.map(String));
  }, []);

  const reloadAfterAccountChange = useCallback(async () => {
    persistor.pause();
    await persistor.flush();
    await persistor.purge();
    dispatch(resetApp());
    resetQueryClient();
    window.location.reload();
  }, [dispatch]);

  const handleSwitchProfile = useCallback(async (targetUserId: number) => {
    const key = `switch-profile-${targetUserId}`;
    message.loading({ content: 'Cambiando de cuenta...', key });
    try {
      await dispatch(switchProfileAsync(targetUserId)).unwrap();
      message.success({ content: 'Cuenta cambiada', key });
      await reloadAfterAccountChange();
    } catch (error) {
      const content = typeof error === 'string' ? error : 'No se pudo cambiar de cuenta';
      message.error({ content, key });
    }
  }, [dispatch, reloadAfterAccountChange]);

  const handleReturnToOrigin = useCallback(async () => {
    const key = 'return-to-origin';
    message.loading({ content: 'Volviendo a tu sesión original...', key });
    try {
      await dispatch(returnToOriginAsync()).unwrap();
      message.success({ content: 'Sesión restaurada', key });
      await reloadAfterAccountChange();
    } catch (error) {
      const content = typeof error === 'string' ? error : 'No se pudo volver a la sesión original';
      message.error({ content, key });
    }
  }, [dispatch, reloadAfterAccountChange]);

  const items = useMemo<NonNullable<MenuProps['items']>>(() => {
    if (!isAuthenticated) {
      return [];
    }

    const entries: NonNullable<MenuProps['items']> = [];

    if (profile) {
      const displayName = [profile.first_name, profile.last_name].filter(Boolean).join(' ')
        || profile.username
        || '';
      entries.push({
        key: 'user-header',
        type: 'group' as const,
        label: (
          <div className="user-menu-header">
            <span className="user-menu-header__name">{displayName}</span>
            {profile.email ? <span className="user-menu-header__detail">{profile.email}</span> : null}
            {profile.phone ? <span className="user-menu-header__detail">{profile.phone}</span> : null}
          </div>
        ),
      });
    }

    if (relationships && (relationships.items.length > 0 || relationships.return_to_original)) {
      entries.push({
        key: 'relationships-header',
        type: 'group' as const,
        label: 'Cuentas vinculadas',
      });

      relationships.items.forEach((item) => {
        const name = [item.first_name, item.last_name].filter(Boolean).join(' ') || `Perfil ${item.id}`;
        const suffix = item.relationship_label ? ` · ${item.relationship_label}` : '';
        entries.push({
          key: `switch-profile:${item.id}`,
          label: `${name}${suffix}`,
          icon: <SwapOutlined />,
        });
      });

      if (relationships.return_to_original) {
        entries.push({
          key: 'return-to-origin',
          label: 'Volver a mi sesión',
          icon: <RollbackOutlined />,
        });
      }

      entries.push({ type: 'divider' });
    }

    if (allowStaffOverride) {
      entries.push({
        key: 'checkout-override',
        label: staffBypassEnabled ? 'Ocultar menú en checkout' : 'Mostrar menú en checkout',
        icon: staffBypassEnabled ? <EyeInvisibleOutlined /> : <EyeOutlined />,
      });
      entries.push({ type: 'divider' });

      if (!staffBypassEnabled) {
        entries.push({ key: 'logout', label: 'Salir', icon: <LogoutOutlined />, danger: true });
        return entries;
      }
    }

    if (isStaff && siteApps.includes('eshop')) {
      entries.push({ key: 'site:eshop', label: 'Tienda', icon: <ShoppingCartOutlined /> });
      if (isMobile) {
        const editorLabel = isEditorMinimized
          ? (hasUnsavedChanges ? 'Restaurar editor (cambios sin guardar)' : 'Restaurar editor')
          : 'Editar tienda';
        entries.push({
          key: 'site:eshop:edit',
          label: editorLabel,
          icon: <BgColorsOutlined />,
        });
      }
      entries.push({
        key: 'site:eshop:dashboard',
        label: 'Panel de Tienda',
        icon: <DashboardOutlined />,
      });
      entries.push({ type: 'divider' });
    }

    if (isStaff && cmsAccess.canAccess) {
      entries.push({ key: 'site:web', label: 'Web', icon: <GlobalOutlined /> });
      entries.push({ type: 'divider' });
    }

    if (isStaff && erpEnabled) {
      entries.push({ key: 'site:erp', label: 'ERP - Gestión Interna', icon: <DatabaseOutlined /> });

      if (showAdvancedToggle) {
        entries.push({
          key: 'toggle-advanced-mode',
          label: (
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
              onClick={(event) => event.stopPropagation()}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography.Text strong style={{ margin: 0 }}>
                  {advancedMode ? 'Modo avanzado' : 'Modo simplificado'}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ margin: 0 }}>
                  {advancedMode ? 'Mostrando todas las pantallas.' : 'Oculta opciones avanzadas del ERP.'}
                </Typography.Text>
              </div>
              <Switch
                checked={advancedMode}
                aria-label="Alternar modo avanzado del ERP"
                onChange={(checked, event) => {
                  event?.stopPropagation();
                  setAdvancedMode(checked);
                  message.info(checked ? 'Modo avanzado activado' : 'Modo simplificado activado');
                }}
              />
            </div>
          ),
        });
      }

      entries.push({ type: 'divider' });
    }

    if (isStaff && previewActive) {
      entries.push({
        key: 'public-preview:close',
        label: 'Cerrar Preview',
        icon: <EyeInvisibleOutlined />,
      });
      entries.push({ type: 'divider' });
    }

    entries.push({
      key: 'theme',
      label: `Apariencia: ${themeModeLabel}`,
      icon: themeModeIcon,
      children: [
        { key: 'theme:light', label: 'Claro', icon: <SunFilled /> },
        { key: 'theme:dark', label: 'Oscuro', icon: <MoonFilled /> },
        { key: 'theme:system', label: 'Sistema', icon: <DesktopOutlined /> },
      ],
    });

    if (isMobile && validTours.length > 0) {
      entries.push({
        key: 'help-tours',
        label: 'Tours guiados',
        icon: <QuestionCircleOutlined />,
        children: validTours.map(({ tourName }) => ({
          key: `tour:${tourName}`,
          label: tourName,
          icon: <i className="fa-solid fa-location-arrow" aria-hidden />,
        })),
      });
    }

    entries.push({ key: 'account', label: 'Mi cuenta', icon: <UserOutlined /> });

    if (isStaff && (isErpContext || isDashboardContext || isCmsContext) && canManageRoles) {
      entries.push({ key: 'site-colors', label: 'Colores del sistema', icon: <BgColorsOutlined /> });
    }

    entries.push({ type: 'divider' });
    entries.push({ key: 'logout', label: 'Salir', icon: <LogoutOutlined />, danger: true });

    return entries;
  }, [
    advancedMode,
    allowStaffOverride,
    canManageRoles,
    cmsAccess.canAccess,
    erpEnabled,
    isAuthenticated,
    isErpContext,
    isDashboardContext,
    isCmsContext,
    isStaff,
    previewActive,
    profile,
    relationships,
    showAdvancedToggle,
    siteApps,
    staffBypassEnabled,
    setAdvancedMode,
    themeModeIcon,
    themeModeLabel,
    isMobile,
    validTours,
    isEditorMinimized,
    hasUnsavedChanges,
  ]);

  const onClick: MenuProps['onClick'] = useCallback((info: Parameters<NonNullable<MenuProps['onClick']>>[0]) => {
    const { domEvent } = info as Parameters<NonNullable<MenuProps['onClick']>>[0];
    const key = String(info.key);
    domEvent.stopPropagation();
    if (key === 'checkout-override') {
      setStaffBypassEnabled((prev) => !prev);
      return;
    }
    if (key === 'toggle-advanced-mode') {
      setAdvancedMode((prev) => !prev);
      return;
    }
    if (key === 'public-preview:close') {
      exitPreview();
      closeUserMenu({ suppressImmediateReopen: true });
      return;
    }
    if (key === 'site-colors') {
      closeUserMenu({ suppressImmediateReopen: true });
      setIsColorModalOpen(true);
      return;
    }
    if (key === 'site:eshop:edit') {
      closeUserMenu({ suppressImmediateReopen: true });
      handleEditTiendaClick();
      return;
    }
    if (key.startsWith('tour:')) {
      const tourName = key.split('tour:')[1];
      const tour = validTours.find((t) => t.tourName === tourName);
      if (tour) {
        closeUserMenu({ suppressImmediateReopen: true });
        void handleTourSelection(tour.stepsRaw);
      }
      return;
    }
    if (key === 'return-to-origin') {
      closeUserMenu({ suppressImmediateReopen: true });
      void handleReturnToOrigin();
      return;
    }
    if (key.startsWith('theme:')) {
      setThemeMode(key.split(':')[1] as ThemeMode);
      closeUserMenu({ suppressImmediateReopen: true });
      return;
    }
    if (key.startsWith('switch-profile:')) {
      const targetUserId = Number(key.split(':')[1]);
      if (!Number.isNaN(targetUserId)) {
        closeUserMenu({ suppressImmediateReopen: true });
        void handleSwitchProfile(targetUserId);
      }
      return;
    }
    try {
      if (key === 'account') {
        closeUserMenu({ suppressImmediateReopen: true });
        if (location.pathname.startsWith('/erp')) {
          navigate('/erp/account');
        } else if (
          location.pathname.startsWith('/web') ||
          location.pathname.startsWith('/cms') ||
          location.pathname.startsWith('/site-builder')
        ) {
          navigate('/cms/account');
        } else {
          navigate('/eshop/account');
        }
      }
      if (key === 'site:web') {
        closeUserMenu({ suppressImmediateReopen: true });
        navigate('/web');
      }
      if (key === 'site:eshop') {
        closeUserMenu({ suppressImmediateReopen: true });
        navigate('/eshop/catalogue');
      }
      if (key === 'site:eshop:dashboard') {
        closeUserMenu({ suppressImmediateReopen: true });
        navigate('/eshop/dashboard');
      }
      if (key === 'site:erp') {
        closeUserMenu({ suppressImmediateReopen: true });
        navigate('/erp');
      }
      if (key === 'site:dashboard') {
        closeUserMenu({ suppressImmediateReopen: true });
        navigate('/dashboard');
      }
    } catch {
      message.error('No se pudo navegar a la sección solicitada');
    }
    if (key === 'logout') {
      closeUserMenu({ suppressImmediateReopen: true });
      setIsLogoutModalOpen(true);
    }
  }, [
    closeUserMenu,
    exitPreview,
    navigate,
    setStaffBypassEnabled,
    setAdvancedMode,
    handleReturnToOrigin,
    handleSwitchProfile,
    setThemeMode,
    handleEditTiendaClick,
    validTours,
    handleTourSelection,
  ]);

  const avatarProps = useMemo(() => {
    if (profile?.avatar_url) {
      return { src: profile.avatar_url };
    }
    return { icon: <UserOutlined /> };
  }, [profile?.avatar_url]);

  if (!isAuthenticated) {
    if (hideNavigation) {
      return null;
    }
    return <LoginButton />;
  }

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      setIsLogoutModalOpen(false);
    } catch {
      message.error('Error al cerrar sesión');
    }
  };

  return (
    <>
      <Dropdown
        open={isUserMenuOpen}
        onOpenChange={handleUserMenuOpenChange}
        trigger={['click']}
        destroyOnHidden
        placement={placement}
        menu={{
          items,
          onClick,
          openKeys: openMenuKeys,
          onOpenChange: handleMenuOpenChange,
        }}
        classNames={{ root: 'user-menu-dropdown' }}
        // overlayStyle={{ minWidth: 260 }}
      >
        {children ||
          (profileStatus === 'loading' ? (
            <Skeleton.Avatar active size={44} shape="circle" />
          ) : (
            <Avatar
              size={44}
              style={{
                cursor: 'pointer',
                background: 'var(--ant-primary-color)',
                color: 'var(--text-color-default)',
              }}
              aria-label="Menú de usuario"
              {...avatarProps}
            />
          ))}
      </Dropdown>
      <Modal
        title="Colores del sistema"
        open={isColorModalOpen}
        onOk={handleSaveColors}
        confirmLoading={isSavingColors}
        okText="Guardar"
        cancelText="Cancelar"
        onCancel={() => setIsColorModalOpen(false)}
        destroyOnHidden
      >
        <ButtonPanelConfigEstilo
          ref={colorPanelRef}
          variant="embedded"
          onAfterSave={() => setIsColorModalOpen(false)}
        />
      </Modal>
      <Modal
        title="¿Cerrar sesión?"
        open={isLogoutModalOpen}
        onOk={handleLogout}
        onCancel={() => setIsLogoutModalOpen(false)}
        okText="Sí, salir"
        cancelText="Cancelar"
      >
        <Typography.Text>Se cerrará tu sesión actual.</Typography.Text>
      </Modal>
    </>
  );
};
